export const initGalleryCarousel = () => {
    const galleryBlocks = document.querySelectorAll<HTMLElement>(
        '[data-selector="gallery-carousel-block"]',
    );

    if (galleryBlocks.length === 0) {
        return;
    }

    galleryBlocks.forEach((block) => {
        new GalleryCarousel(block);
    });
};

export class GalleryCarousel {
    private wrapper: HTMLElement;
    private activeIndex: number = 0;
    private GAP = 16;
    private TRANSITION_DURATION_MS = 3000;
    private originalRange: [start: number, end: number] = [0, 0];
    private isInViewport: boolean = false;

    constructor(private container: HTMLElement) {
        this.wrapper = container.querySelector<HTMLElement>(
            '[data-selector="gallery-wrapper"]',
        )!;

        this.init();
    }

    enabledTransitions() {
        this.container.dataset.state = "animate";
    }

    disabledTransitions() {
        this.container.dataset.state = "stale";
    }

    init() {
        this.createInfiniteLoop();
        setTimeout(() => {
            this.centerActiveItem();
            this.showCarousel();
            this.setupIntersectionObserver();
        }, 100);

        setTimeout(() => {
            this.enabledTransitions();
        }, 200);
    }

    setupIntersectionObserver() {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    this.isInViewport = entry.isIntersecting;

                    if (this.isInViewport) {
                        this.run();
                    }
                });
            },
            { threshold: 0.1 },
        );

        const galleryContainer = this.wrapper.closest(
            '[data-slot="gallery-block"]',
        );
        if (galleryContainer) {
            observer.observe(galleryContainer);
        }
    }

    run() {
        if (!this.isInViewport) {
            return;
        }
        this.activeIndex++;

        /**
         * Reset to start position.
         */
        if (this.activeIndex >= this.originalRange[1]) {
            this.activeIndex = this.originalRange[0] - 1;
            this.disabledTransitions();
            this.centerActiveItem();
            setTimeout(() => {
                this.enabledTransitions();
                this.run();
            }, 100);
        } else {
            this.centerActiveItem();
            setTimeout(() => this.run(), this.TRANSITION_DURATION_MS);
        }
    }

    private showCarousel(): void {
        if (this.wrapper) {
            this.container?.classList.remove("invisible");
            this.container?.classList.add("visible");
        }
    }

    private getItems(): HTMLElement[] {
        return Array.from(
            this.wrapper.querySelectorAll(
                '[data-selector="gallery-item"]',
            ) as NodeListOf<HTMLElement>,
        );
    }

    private createInfiniteLoop() {
        const windowWidth = this.container.clientWidth;
        const items = this.getItems();
        const totalItems = items.length;

        if (items.length === 0) return;

        const spaceNeededBeforeFirstItem = windowWidth;

        const totalItemsWidth = items.reduce((total, item, index) => {
            const [width] = this.calculateBaseItemSize(item);
            const gap = index < items.length - 1 ? 16 : 0;

            return total + width + gap;
        }, 0);

        const fullSetsToPrepend = Math.floor(
            spaceNeededBeforeFirstItem / totalItemsWidth,
        );
        const remainingSpace =
            spaceNeededBeforeFirstItem - fullSetsToPrepend * totalItemsWidth;

        for (let setIndex = 0; setIndex < fullSetsToPrepend; setIndex++) {
            for (let i = items.length - 1; i >= 0; i--) {
                const clone = items[i].cloneNode(true) as HTMLElement;
                clone.setAttribute("data-cloned", "true");
                this.wrapper.insertBefore(clone, this.wrapper.firstChild);
            }
        }

        let currentWidth = 0;
        const itemsToClone = [];

        for (let i = items.length - 1; i >= 0; i--) {
            const [itemWidth] = this.calculateBaseItemSize(items[i]);
            const gap = 16;

            if (currentWidth + itemWidth + gap <= remainingSpace) {
                itemsToClone.push(i);
                currentWidth += itemWidth + gap;
            } else {
                break;
            }
        }

        for (let i = 0; i < itemsToClone.length; i++) {
            const clone = items[itemsToClone[i]].cloneNode(true) as HTMLElement;
            clone.setAttribute("data-cloned", "true");
            this.wrapper.insertBefore(clone, this.wrapper.firstChild);
        }

        this.activeIndex =
            fullSetsToPrepend * items.length + itemsToClone.length;

        this.originalRange = [this.activeIndex, totalItems + this.activeIndex];

        const screensToFill = 1.5; // 1 full screen plus half for buffer
        const itemsNeededAfter = Math.ceil(
            (windowWidth * screensToFill) / (totalItemsWidth / items.length),
        );

        const maxItemsToAppend = Math.max(items.length * 2, itemsNeededAfter);

        for (let i = 0; i < maxItemsToAppend; i++) {
            const originalIndex = i % items.length;
            const clone = items[originalIndex].cloneNode(true) as HTMLElement;
            clone.setAttribute("data-cloned", "true");
            this.wrapper.appendChild(clone);
        }
    }

    centerActiveItem() {
        const fullWidth = this.container.clientWidth;
        const centerX = fullWidth / 2;
        const items = this.getItems();
        items.forEach((el, index) => {
            const [, h] = this.calculateItemSize(index, el);
            el.style.height = `${h}px`;
            el.clientHeight; // Force reflow
        });
        const elementsBefore = items.slice(0, this.activeIndex);
        const item = items[this.activeIndex];
        const offset = elementsBefore.reduce((total, element, index) => {
            const [width] = this.calculateItemSize(index, element);
            return total + width + this.GAP;
        }, 0);
        const [itemWidth] = this.calculateItemSize(this.activeIndex, item);
        const xPos = Math.round(offset - centerX + itemWidth / 2) * -1;

        this.wrapper.style.transform = `translateX(${xPos}px)`;
    }

    get itemFullHeight(): number {
        return this.container.clientHeight;
    }

    get itemSiblingHeight(): number {
        return this.itemFullHeight * 0.85;
    }

    get itemBaseHeight(): number {
        return this.itemFullHeight * 0.7;
    }

    calculateBaseItemSize(
        index: number | HTMLElement,
    ): [width: number, height: number] {
        const element =
            typeof index === "number" ? this.getItems()[index] : index;
        const imgWidth = parseInt(element.getAttribute("width") || "1", 10);
        const imgHeight = parseInt(element.getAttribute("height") || "1");
        const baseHeight = this.itemBaseHeight;
        const imgRatio = imgWidth / imgHeight;
        const expectedWidth = baseHeight * imgRatio;

        return [expectedWidth, baseHeight];
    }

    calculateItemSize(
        index: number,
        providedElement?: HTMLElement,
    ): [width: number, height: number] {
        const element = providedElement ?? this.getItems()[index];
        const [width, height] = this.calculateBaseItemSize(element);
        const expectedHeight =
            index === this.activeIndex
                ? this.itemFullHeight
                : index === this.activeIndex - 1 ||
                    index === this.activeIndex + 1
                  ? this.itemSiblingHeight
                  : this.itemBaseHeight;
        const imgRatio = width / height;
        const expectedWidth = expectedHeight * imgRatio;

        return [expectedWidth, expectedHeight];
    }
}
